import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import flatpickr from "flatpickr";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";

import DatePicker from "../../../../shared/ui/form/date-picker";
import PhoneInput from "../../../../shared/ui/form/group-input/PhoneInput";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import TimezoneSelect from "../../../../shared/ui/form/TimezoneSelect";
import WhatsAppNumberField from "../../../../shared/ui/form/WhatsAppNumberField";
import { COUNTRIES, sortedCountries, timezoneForCountry } from "../../../../shared/domain/countries";
import { PRESET_PRONOUNS, type PersonDetails, type PersonField } from "../../domain/PersonDetails";

interface Props {
    /** Prefixes every field's id, so two forms on one page never share one. */
    idPrefix: string;
    value: PersonDetails;
    onChange: (changes: Partial<PersonDetails>) => void;
    /** Field → i18n key, from `validatePatient`. */
    errors: Record<string, string>;
    /**
     * A field was left (a date, once picked), with the form as it now stands —
     * for a form that checks each field as it goes.
     */
    onBlur?: (field: PersonField, value: PersonDetails) => void;
    /** The person switched WhatsApp off in their portal. */
    refusedWhatsApp?: boolean;
    /** Explains what the patient's timezone is for, under it. */
    showTimezoneHint?: boolean;
    autoFocus?: boolean;
}

const SELECT_CLASS = "field-base field-default h-11 w-full px-4 text-sm";

/**
 * The person every patient form asks about — a therapist's new patient, their
 * edit of one, a centre's student — the same fields, with the same labels,
 * required marks and errors. Presentational: the form around it holds the
 * values, decides when to check them, and adds what only it asks (a course,
 * whose patient it is, the centre's own fields).
 */
export default function PersonFields({
    idPrefix,
    value,
    onChange,
    errors,
    onBlur,
    refusedWhatsApp = false,
    showTimezoneHint = false,
    autoFocus = false,
}: Props) {
    const { t, i18n } = useTranslation();
    const id = (field: string) => `${idPrefix}-${field.replace(/_/g, "-")}`;
    const countryOptions = useMemo(() => sortedCountries(i18n?.language ?? "es"), [i18n?.language]);
    const dialCodes = useMemo(() => COUNTRIES.map(({ code, dialCode }) => ({ code, label: dialCode })), []);

    // Handlers that outlive a render (the date picker's) read the latest props.
    const latest = useRef({ value, onChange, onBlur });
    useEffect(() => {
        latest.current = { value, onChange, onBlur };
    });

    /**
     * The country seeds the timezone only until somebody answers for
     * themselves. Tracked as a flag rather than by comparing the field against
     * what the country would suggest: re-picking exactly the suggested zone is
     * a choice, and the next country change must not quietly undo it. A zone
     * already on the record counts as chosen.
     */
    const timezoneChosen = useRef(Boolean(value.timezone));
    const changeCountry = (country: string) => {
        const suggested = timezoneForCountry(country);
        onChange(!timezoneChosen.current && suggested ? { country, timezone: suggested } : { country });
    };

    // Pronouns outside the list are written in; "other" opens the box for them.
    const [customPronouns, setCustomPronouns] = useState(
        () => Boolean(value.pronouns) && !(PRESET_PRONOUNS as readonly string[]).includes(value.pronouns)
    );

    const pickBirth = useCallback((dates: Date[]) => {
        const { value: current, onChange: change, onBlur: blur } = latest.current;
        const date_of_birth = dates[0] ? flatpickr.formatDate(dates[0], "Y-m-d") : "";
        change({ date_of_birth });
        blur?.("date_of_birth", { ...current, date_of_birth });
    }, []);

    const blur = (field: PersonField, fieldValue: string) => onBlur?.(field, { ...value, [field]: fieldValue });

    const errorId = (field: string) => `${id(field)}-error`;
    const describedBy = (field: PersonField) => (errors[field] ? errorId(field) : undefined);
    const error = (field: PersonField) =>
        errors[field] ? (
            <p id={errorId(field)} className="mt-1 text-xs text-status-critical">
                {t(errors[field])}
            </p>
        ) : null;
    const required = <span className="text-status-critical"> *</span>;

    const text = (field: PersonField, label: string) => (
        <div>
            <Label htmlFor={id(field)}>{label}</Label>
            <Input
                id={id(field)}
                value={value[field]}
                onChange={(e) => onChange({ [field]: e.target.value })}
                onBlur={(e) => blur(field, e.target.value)}
                error={Boolean(errors[field])}
                aria-invalid={Boolean(errors[field])}
                aria-describedby={describedBy(field)}
            />
            {error(field)}
        </div>
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <Label htmlFor={id("first-name")}>
                        {t("patient.name")}
                        {required}
                    </Label>
                    <Input
                        id={id("first-name")}
                        value={value.first_name}
                        onChange={(e) => onChange({ first_name: e.target.value })}
                        onBlur={(e) => blur("first_name", e.target.value)}
                        error={Boolean(errors.first_name)}
                        aria-invalid={Boolean(errors.first_name)}
                        aria-describedby={describedBy("first_name")}
                        autoFocus={autoFocus}
                    />
                    {error("first_name")}
                </div>
                <div>
                    <Label htmlFor={id("last-name")}>
                        {t("patient.lastName")}
                        {required}
                    </Label>
                    <Input
                        id={id("last-name")}
                        value={value.last_name}
                        onChange={(e) => onChange({ last_name: e.target.value })}
                        onBlur={(e) => blur("last_name", e.target.value)}
                        error={Boolean(errors.last_name)}
                        aria-invalid={Boolean(errors.last_name)}
                        aria-describedby={describedBy("last_name")}
                    />
                    {error("last_name")}
                </div>
                <div>
                    <Label htmlFor={id("email")}>
                        {t("patient.email")}
                        {required}
                    </Label>
                    <Input
                        id={id("email")}
                        type="email"
                        placeholder="patient@example.com"
                        startIcon={<Mail className="size-5" aria-hidden="true" />}
                        value={value.contact_email}
                        onChange={(e) => onChange({ contact_email: e.target.value })}
                        onBlur={(e) => blur("contact_email", e.target.value)}
                        error={Boolean(errors.contact_email)}
                        aria-invalid={Boolean(errors.contact_email)}
                        aria-describedby={describedBy("contact_email")}
                    />
                    {error("contact_email")}
                </div>
                <div>
                    <Label htmlFor={id("phone")}>
                        {t("patient.phone")}
                        {required}
                    </Label>
                    <div onBlur={() => blur("contact_phone", value.contact_phone)}>
                        <PhoneInput
                            id={id("phone")}
                            aria-invalid={Boolean(errors.contact_phone)}
                            aria-describedby={describedBy("contact_phone")}
                            selectPosition="start"
                            countries={dialCodes}
                            placeholder="000 000 000"
                            value={value.contact_phone}
                            onChange={(contact_phone) => onChange({ contact_phone })}
                        />
                    </div>
                    {error("contact_phone")}
                </div>
                <div className="sm:col-span-2">
                    <WhatsAppNumberField
                        value={value.whatsapp_phone}
                        onChange={(whatsapp_phone) => onChange({ whatsapp_phone })}
                        onBlur={() => blur("whatsapp_phone", value.whatsapp_phone)}
                        error={errors.whatsapp_phone}
                        refusedByPatient={refusedWhatsApp}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                    <Label htmlFor={id("pronouns")}>{t("patient.pronouns")}</Label>
                    <select
                        id={id("pronouns")}
                        value={customPronouns ? "other" : value.pronouns}
                        onChange={(e) => {
                            const picked = e.target.value;
                            setCustomPronouns(picked === "other");
                            onChange({ pronouns: picked });
                        }}
                        className={SELECT_CLASS}
                    >
                        <option value="">{t("common.select")}</option>
                        <option value="he/him">{t("patient.pronounsHeHim")}</option>
                        <option value="she/her">{t("patient.pronounsSheHer")}</option>
                        <option value="they/them">{t("patient.pronounsTheyThem")}</option>
                        <option value="other">{t("patient.sexOther")}</option>
                    </select>
                    {customPronouns && (
                        <div className="mt-2">
                            <Label htmlFor={id("pronouns-custom")} className="sr-only">
                                {t("patient.pronounsPlaceholder")}
                            </Label>
                            <Input
                                id={id("pronouns-custom")}
                                placeholder={t("patient.pronounsPlaceholder")}
                                value={value.pronouns === "other" ? "" : value.pronouns}
                                onChange={(e) => onChange({ pronouns: e.target.value || "other" })}
                            />
                        </div>
                    )}
                </div>
                <div>
                    <DatePicker
                        id={id("dob")}
                        label={t("patient.dateOfBirth")}
                        defaultDate={value.date_of_birth || undefined}
                        onChange={pickBirth}
                    />
                    {error("date_of_birth")}
                </div>
                <div>
                    <Label htmlFor={id("sex")}>{t("patient.sex")}</Label>
                    <select
                        id={id("sex")}
                        value={value.sex}
                        onChange={(e) => onChange({ sex: e.target.value })}
                        className={SELECT_CLASS}
                    >
                        <option value="">{t("common.select")}</option>
                        <option value="male">{t("patient.sexMale")}</option>
                        <option value="female">{t("patient.sexFemale")}</option>
                        <option value="other">{t("patient.sexOther")}</option>
                    </select>
                </div>
            </div>

            <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <legend className="mb-4 w-full border-b border-border-subtle pb-2 text-sm font-semibold text-content-primary">
                    {t("patient.address")}
                </legend>
                <div className="sm:col-span-2">{text("street", t("patient.street"))}</div>
                {text("city", t("patient.city"))}
                {text("state", t("patient.state"))}
                {text("postal_code", t("patient.postalCode"))}
                <div>
                    <Label htmlFor={id("country")}>{t("patient.country")}</Label>
                    <select
                        id={id("country")}
                        name="country"
                        value={value.country}
                        onChange={(e) => changeCountry(e.target.value)}
                        className={SELECT_CLASS}
                    >
                        {countryOptions.map((country) => (
                            <option key={country.code} value={country.code}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <Label htmlFor={id("timezone")}>{t("patient.timezone")}</Label>
                    <TimezoneSelect
                        id={id("timezone")}
                        name="timezone"
                        value={value.timezone}
                        onChange={(timezone) => {
                            timezoneChosen.current = true;
                            onChange({ timezone });
                        }}
                        placeholder={t("patient.timezoneUnset")}
                        className={SELECT_CLASS}
                    />
                    {showTimezoneHint && (
                        <p className="mt-1 text-xs text-content-tertiary">{t("patient.timezoneHint")}</p>
                    )}
                </div>
            </fieldset>
        </div>
    );
}
